// File: frontend/src/components/ExecuteTestcaseModal.jsx

import React, { useState, useEffect } from "react";
import axios from "../axios"; // ← your Axios instance (baseURL = "/api")
import { jwtDecode } from "jwt-decode"; // ← jwtDecode is the default export

const ExecuteTestcaseModal = () => {
    // ----- State -----
    const [projects, setProjects] = useState([]);
    const [suites, setSuites] = useState([]);
    const [testcases, setTestcases] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null); // ← start with null
    const [selectedSuiteName, setSelectedSuiteName] = useState("");
    const [testerName, setTesterName] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const [page, setPage] = useState(1);
    const PER_PAGE = 25;
    const totalPages = Math.ceil(testcases.length / PER_PAGE);
    const pagedTestcases = testcases.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    // ----- Auto‐populate testerName from JWT -----
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        try {
            const decoded = jwtDecode(token);
            const nameFromToken =
                decoded.name ||
                decoded.username ||
                decoded.email ||
                decoded.preferred_username ||
                decoded.sub ||
                "";
            setTesterName(nameFromToken);
        } catch (e) {
            console.warn("Failed to decode JWT:", e);
        }
    }, []);

    // Whenever testcases array changes, reset to page 1
    useEffect(() => {
        setPage(1);
    }, [testcases]);

    // On mount, fetch projects
    useEffect(() => {
        fetchProjects();
    }, []);

    // ----- Fetch Helpers -----
    const fetchProjects = async () => {
        setIsLoading(true);
        try {
            const { data } = await axios.get("/projects"); // GET /api/projects

            if (data.success && Array.isArray(data.projects)) {
                setProjects(data.projects);
            } else if (Array.isArray(data)) {
                setProjects(data);
            } else {
                setProjects([]);
            }
        } catch (err) {
            console.error("Failed to load projects:", err);
            alert("Failed to load projects.");
            setProjects([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleProjectSelect = async (rawProj) => {
        // Determine which field actually holds the ID:
        const projectKey = rawProj.project_id ?? rawProj.id ?? rawProj._id;
        console.log("Clicked project object:", rawProj, "→ using ID:", projectKey);

        setSelectedProject(projectKey);
        setSelectedSuiteName("");
        setTestcases([]);
        setSuites([]);

        if (projectKey === null || projectKey === undefined) return;

        setIsLoading(true);
        try {
            const { data } = await axios.get(`/suites/${projectKey}`); // GET /api/suites/:projectKey

            let fetchedSuites = [];
            if (data.success && Array.isArray(data.suites)) {
                fetchedSuites = data.suites;
            } else if (Array.isArray(data)) {
                fetchedSuites = data;
            }
            console.log("Fetched suites for project ID", projectKey, "→", fetchedSuites);
            setSuites(fetchedSuites);
        } catch (err) {
            console.error("Failed to load suites:", err);
            alert("Failed to load suites.");
            setSuites([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSuiteSelect = async (suiteName) => {
        setSelectedSuiteName(suiteName);
        setTestcases([]);

        if (!suiteName) return;

        setIsLoading(true);
        try {
            const { data } = await axios.get(`/testcases/${encodeURIComponent(suiteName)}`);
            let fetched = [];
            if (data.success && Array.isArray(data.testcases)) {
                fetched = data.testcases;
            } else if (Array.isArray(data)) {
                fetched = data;
            }
            console.log("Fetched testcases for suite", suiteName, ":", fetched);

            // Enrich each testcase with local fields
            const enriched = fetched.map((tc) => ({
                ...tc,
                actualResult: "",
                status: "Pass",
                screenshotFile: null,
                screenshotPreview: null,
            }));
            setTestcases(enriched);
        } catch (err) {
            console.error("Failed to load test cases:", err);
            alert("Failed to load test cases.");
            setTestcases([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (index, field, value) => {
        const copy = [...testcases];
        copy[index][field] = value;
        setTestcases(copy);
    };

    const handlePaste = (index, e) => {
        for (let item of e.clipboardData.items) {
            if (item.type.startsWith("image/")) {
                const file = item.getAsFile();
                const reader = new FileReader();
                reader.onload = () => {
                    handleInputChange(index, "screenshotFile", file);
                    handleInputChange(index, "screenshotPreview", reader.result);
                };
                reader.readAsDataURL(file);
                break;
            }
        }
    };

    const handleFileSelect = (index, e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            handleInputChange(index, "screenshotFile", file);
            handleInputChange(index, "screenshotPreview", reader.result);
        };
        reader.readAsDataURL(file);
    };

    const executeOne = async (tc) => {
        if (!testerName.trim()) {
            return alert("Tester name is missing.");
        }
        if (!tc.actualResult.trim()) {
            return alert("Actual Result is required.");
        }
        if (!tc.screenshotFile) {
            return alert("Screenshot is required.");
        }

        const fd = new FormData();
        fd.append("testcaseId", tc.id);
        fd.append("title", tc.title);
        fd.append("description", tc.description);
        fd.append("project", selectedProject);
        fd.append("suite_name", selectedSuiteName);
        fd.append("testerName", testerName);
        fd.append("actualResult", tc.actualResult);
        fd.append("status", tc.status);
        fd.append("executedAt", new Date().toISOString());
        fd.append("screenshot", tc.screenshotFile);

        try {
            await axios.post("/execute", fd); // POST /api/execute
            setSuccessMessage(`Testcase "${tc.title}" executed successfully.`);
        } catch (error) {
            console.error(error);
            alert("Failed to execute testcase: " + tc.title);
        }
    };

    const executeAll = async () => {
        if (!testerName.trim()) {
            return alert("Tester name is missing.");
        }
        for (let tc of testcases) {
            if (!tc.actualResult.trim() || !tc.screenshotFile) {
                return alert(
                    "Please fill Actual Result and add screenshot for all testcases."
                );
            }
        }
        try {
            await Promise.all(
                testcases.map((tc) => {
                    const fd = new FormData();
                    fd.append("testcaseId", tc.id);
                    fd.append("title", tc.title);
                    fd.append("description", tc.description);
                    fd.append("project", selectedProject);
                    fd.append("suite_name", selectedSuiteName);
                    fd.append("testerName", testerName);
                    fd.append("actualResult", tc.actualResult);
                    fd.append("status", tc.status);
                    fd.append("executedAt", new Date().toISOString());
                    fd.append("screenshot", tc.screenshotFile);
                    return axios.post("/execute", fd);
                })
            );
            setSuccessMessage("All testcases executed successfully.");
        } catch {
            alert("One or more testcases failed");
        }
    };

    return (
        <div className="p-6 w-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-gray-800">Execute Testcases</h2>
                {testcases.length > 0 && (
                    <button
                        type="button"
                        onClick={executeAll}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow"
                    >
                        Execute All
                    </button>
                )}
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="mb-4 text-green-700 bg-green-100 p-3 rounded">
                    {successMessage}
                </div>
            )}

            {/* STEP 1: Project Selection Area */}
            {selectedProject === null ? (
                <section className="mb-6 bg-white p-4 rounded shadow">
                    <h3 className="text-xl font-semibold text-gray-700 mb-3">
                        Select a Project
                    </h3>

                    {isLoading ? (
                        <div className="text-center text-gray-500 py-4">Loading projects…</div>
                    ) : projects.length === 0 ? (
                        <div className="text-center text-gray-500 py-4">No projects available</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {projects.map((proj) => {
                                // Determine which field really holds the ID:
                                const projectKey = proj.project_id ?? proj.id ?? proj._id;
                                return (
                                    <button
                                        key={projectKey}
                                        type="button"
                                        onClick={() => handleProjectSelect(proj)}
                                        className={`border rounded-lg p-2 text-left transition-shadow w-full ${
                                            String(selectedProject) === String(projectKey)
                                                ? "bg-indigo-100 border-indigo-400 shadow-md"
                                                : "bg-white border-gray-200 hover:shadow-lg"
                                        }`}
                                    >
                                        <p className="text-lg font-semibold text-indigo-700 truncate">
                                            {proj.project_name}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </section>
            ) : (
                <section className="mb-6 bg-white p-4 rounded shadow flex items-center justify-between">
                    <p className="text-lg text-gray-800">
                        Project:{" "}
                        <span className="font-semibold text-indigo-700">
              {
                  // Find name by matching same key logic:
                  (() => {
                      const found = projects.find((p) => {
                          const key = p.project_id ?? p.id ?? p._id;
                          return String(key) === String(selectedProject);
                      });
                      return found?.project_name || "";
                  })()
              }
            </span>
                    </p>
                    <button
                        type="button"
                        onClick={() => {
                            setSelectedProject(null);
                            setSelectedSuiteName("");
                            setSuites([]);
                            setTestcases([]);
                        }}
                        className="text-sm px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                    >
                        Change Project
                    </button>
                </section>
            )}

            {/* STEP 2: Suite Dropdown */}
            {selectedProject !== null && (
                <section className="mb-6 bg-white p-4 rounded shadow">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block font-medium text-gray-700 mb-1">
                                Suite
                            </label>
                            <select
                                value={selectedSuiteName}
                                onChange={(e) => handleSuiteSelect(e.target.value)}
                                disabled={selectedProject === null}
                                className="w-full border rounded px-3 py-2 mt-1 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                            >
                                <option value="">Select a Suite</option>
                                {suites.map((s) => (
                                    <option key={s.suite_id} value={s.name}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </section>
            )}

            {/* STEP 3: Testcase Table */}
            {isLoading && <p className="text-center text-gray-500">Loading…</p>}

            {testcases.length > 0 && (
                <div className="overflow-auto bg-white rounded shadow mb-4 w-full">
                    <table className="w-full min-w-full divide-y divide-gray-200 table-auto">
                        <thead className="bg-gray-50 sticky top-0">
                        <tr>
                            <th className="px-4 py-2">Title</th>
                            <th className="px-4 py-2">Description</th>
                            <th className="px-4 py-2">Steps</th>
                            <th className="px-4 py-2">Expected</th>
                            <th className="px-4 py-2">Actual</th>
                            <th className="px-4 py-2">Screenshot</th>
                            <th className="px-4 py-2">Status</th>
                            <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        {pagedTestcases.map((tc, idx) => (
                            <tr key={tc.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 break-words">{tc.title}</td>
                                <td className="px-4 py-2 break-words">{tc.description}</td>
                                <td className="px-4 py-2 whitespace-pre-wrap">{tc.steps}</td>
                                <td className="px-4 py-2">{tc.expected_result}</td>
                                <td className="px-4 py-2">
                    <textarea
                        rows={2}
                        value={tc.actualResult}
                        onChange={(e) =>
                            handleInputChange(
                                (page - 1) * PER_PAGE + idx,
                                "actualResult",
                                e.target.value
                            )
                        }
                        className="w-full border rounded px-2 py-1 focus:ring-indigo-400"
                    />
                                </td>
                                <td className="px-4 py-2">
                                    <div
                                        onPaste={(e) => handlePaste((page - 1) * PER_PAGE + idx, e)}
                                        className="h-24 border-2 border-dashed rounded bg-gray-50 p-2 flex flex-col items-center justify-center cursor-pointer"
                                    >
                                        {tc.screenshotPreview ? (
                                            <img src={tc.screenshotPreview} alt="preview" className="max-h-full" />
                                        ) : (
                                            <>
                                                <span className="text-xs">Ctrl+V to paste</span>
                                                <span className="text-xs">or upload</span>
                                            </>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFileSelect((page - 1) * PER_PAGE + idx, e)}
                                            className="mt-1 text-xs"
                                        />
                                    </div>
                                </td>
                                <td className="px-4 py-2">
                                    <select
                                        value={tc.status}
                                        onChange={(e) =>
                                            handleInputChange(
                                                (page - 1) * PER_PAGE + idx,
                                                "status",
                                                e.target.value
                                            )
                                        }
                                        className={`px-2 py-1 rounded focus:ring-2 ${
                                            tc.status === "Pass" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                        }`}
                                    >
                                        <option>Pass</option>
                                        <option>Fail</option>
                                    </select>
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <button
                                        type="button"
                                        onClick={() => executeOne(tc)}
                                        disabled={!tc.actualResult.trim() || !tc.screenshotFile}
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded disabled:opacity-50"
                                    >
                                        Execute
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {testcases.length > PER_PAGE && (
                <div className="flex justify-center items-center space-x-4 mb-6 w-full">
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span>
            Page {page} of {totalPages}
          </span>
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* If no testcases but suite selected */}
            {!isLoading && selectedSuiteName && testcases.length === 0 && (
                <p className="text-center">No testcases found for “{selectedSuiteName}.”</p>
            )}
        </div>
    );
};

export default ExecuteTestcaseModal;
